import * as Icons from '@ant-design/icons';
import { Collapse, InputNumber, Table, Tooltip } from 'antd';
import { DiscountCampaign } from '../GeneralRule';
import { HttpError, useTable } from '@refinedev/core';
import { IProduct } from 'interfaces/products';
import { Stack } from '@qcx/ui';

const { Panel } = Collapse;

export const RenderManyDiscounts = ({
  discounts,
  resource_type,
  hideException
}: {
  discounts: DiscountCampaign[];
  resource_type: string;
  hideException?: boolean;
}) => {
  const filterInvoiceDueRules = (value?: string[] | string) => {
    if (Array.isArray(value)) {
      return value.filter(v => v.toString() !== '1')?.length > 0;
    }

    return value?.toString() !== '1';
  };

  const currentDiscount = discounts.find(d =>
    resource_type === 'invoice_due'
      ? d.rules.find(
          r =>
            r?.resource_type === 'invoice_due' &&
            filterInvoiceDueRules(r?.value)
        )
      : d.rules.find(
          r =>
            r?.resource_type === 'invoice_due' &&
            !filterInvoiceDueRules(r?.value)
        )
  );
  const currentConfig = currentDiscount?.rules?.find(
    d => d.resource_type === resource_type
  );

  const { tableQueryResult } = useTable<IProduct, HttpError>({
    resource: 'products',

    pagination: {
      pageSize: 5
    },

    filters: {
      initial: [
        {
          field: 'id',
          operator: 'eq',
          value: currentConfig?.value || []
        }
      ]
    }
  });

  const products = tableQueryResult?.data?.data ?? [];

  return (
    <Stack css={{ w: '$full', h: 'fit-content' }}>
      <div className="border rounded border-solid divide-y border-[#EAECF0] w-full">
        <Stack
          css={{
            p: '$4',
            d: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {resource_type === 'invoice_due' ? (
            <>
              <p className="flex items-center gap-2 m-0 font-semibold text-sm leading-6 text-[rgba(0,0,0,0.85)]">
                <span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.99935 5.00002V10L13.3327 11.6667M18.3327 10C18.3327 14.6024 14.6017 18.3334 9.99935 18.3334C5.39698 18.3334 1.66602 14.6024 1.66602 10C1.66602 5.39765 5.39698 1.66669 9.99935 1.66669C14.6017 1.66669 18.3327 5.39765 18.3327 10Z"
                      stroke="black"
                      strokeOpacity="0.85"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                Inadimplência maior que
                <InputNumber
                  size="large"
                  name="inoice_value"
                  className="max-w-[130px]"
                  addonAfter="dias"
                  min={31}
                  disabled
                  value={
                    currentDiscount?.rules?.find(
                      r => r.resource_type === 'invoice_due'
                    )?.value
                  }
                />
                <Tooltip title="Esse é o desconto aplicado em toda a prateleira. Podendo ser sobrescrito pelo desconto por produto.">
                  <Icons.QuestionCircleOutlined />
                </Tooltip>
              </p>
              <InputNumber
                size="large"
                name="invoice_discount_value"
                className="max-w-[100px]"
                addonAfter="%"
                disabled
                value={currentDiscount?.discount_config.value as string}
              />
            </>
          ) : (
            <>
              <p className="flex items-center gap-2 m-0 font-semibold text-sm leading-6 text-[rgba(0,0,0,0.85)]">
                <span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_3137_35623)">
                      <path
                        d="M7.49935 7.50008H7.50768M12.4994 12.5001H12.5077M13.3327 6.66675L6.66602 13.3334M14.917 4.1655C15.0886 4.58053 15.418 4.91041 15.8327 5.08264L17.287 5.68506C17.7021 5.85698 18.0318 6.18674 18.2037 6.6018C18.3757 7.01685 18.3757 7.4832 18.2037 7.89826L17.6018 9.35158C17.4298 9.76682 17.4296 10.2336 17.6023 10.6487L18.2032 12.1016C18.2885 12.3071 18.3324 12.5275 18.3324 12.75C18.3324 12.9726 18.2886 13.1929 18.2035 13.3985C18.1183 13.6041 17.9935 13.7909 17.8361 13.9483C17.6787 14.1056 17.4919 14.2304 17.2863 14.3155L15.833 14.9175C15.418 15.0891 15.0881 15.4185 14.9159 15.8332L14.3135 17.2876C14.1416 17.7027 13.8118 18.0324 13.3968 18.2043C12.9817 18.3763 12.5154 18.3763 12.1004 18.2043L10.6471 17.6023C10.232 17.4308 9.76587 17.4312 9.35108 17.6033L7.89676 18.2049C7.48195 18.3764 7.01602 18.3763 6.60132 18.2045C6.18662 18.0327 5.85706 17.7033 5.68503 17.2887L5.08245 15.8339C4.91085 15.4189 4.58147 15.089 4.16672 14.9168L2.7124 14.3143C2.29754 14.1425 1.9679 13.813 1.79592 13.3981C1.62394 12.9833 1.62371 12.5172 1.79527 12.1022L2.39724 10.6489C2.56873 10.2338 2.56838 9.76762 2.39626 9.35282L1.79516 7.8974C1.70993 7.69183 1.66605 7.47148 1.66602 7.24894C1.66598 7.0264 1.70979 6.80604 1.79495 6.60044C1.88011 6.39484 2.00494 6.20804 2.16232 6.05071C2.3197 5.89338 2.50654 5.7686 2.71215 5.6835L4.16543 5.08151C4.58007 4.91006 4.90975 4.58108 5.0821 4.1668L5.6845 2.71242C5.85642 2.29737 6.18616 1.96761 6.6012 1.79569C7.01624 1.62377 7.48257 1.62377 7.89761 1.79569L9.35089 2.39768C9.76594 2.56918 10.2321 2.56882 10.6469 2.3967L12.1018 1.79662C12.5168 1.6248 12.983 1.62483 13.398 1.79672C13.813 1.96861 14.1427 2.29827 14.3146 2.71322L14.9172 4.16802L14.917 4.1655ZM7.91602 7.50008C7.91602 7.7302 7.72947 7.91675 7.49935 7.91675C7.26923 7.91675 7.08268 7.7302 7.08268 7.50008C7.08268 7.26996 7.26923 7.08341 7.49935 7.08341C7.72947 7.08341 7.91602 7.26996 7.91602 7.50008ZM12.916 12.5001C12.916 12.7302 12.7295 12.9167 12.4994 12.9167C12.2692 12.9167 12.0827 12.7302 12.0827 12.5001C12.0827 12.27 12.2692 12.0834 12.4994 12.0834C12.7295 12.0834 12.916 12.27 12.916 12.5001Z"
                        stroke="black"
                        strokeOpacity="0.85"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3137_35623">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </span>
                Valor Base
                <Tooltip title="Esse é o desconto base de toda prateleira. Podendo ser sobrescrito pelo desconto por produto.">
                  <Icons.QuestionCircleOutlined />
                </Tooltip>
              </p>
              <InputNumber
                size="large"
                className="max-w-[100px]"
                addonAfter="%"
                value={currentDiscount?.discount_config.value as string}
                disabled
              />
            </>
          )}
        </Stack>
        {!hideException && (
          <Collapse>
            <Panel header="Exceto para" key="exception" className="p-0">
              <div className="border-b border-[#EAECF0] w-full">
                <Stack
                  css={{
                    pb: '$2',
                    d: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <p className="m-0 font-semibold text-sm leading-6 text-[rgba(0,0,0,0.85)]">
                    Lista de Exceção
                  </p>
                </Stack>
              </div>
              <Table
                showHeader={false}
                dataSource={currentConfig?.value?.length ? products : []}
                className="w-full"
              >
                <Table.Column key="name" dataIndex="name" className="w-full" />
              </Table>
            </Panel>
          </Collapse>
        )}
      </div>
    </Stack>
  );
};
